// Состояние приложения: навигация (стек экранов), текущий подбор и данные профиля.

import { onSite } from './web.ts';
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { api, type AppConfig, type Design, type Me, type PickT, type Size, type Taste } from './api.ts';
import { track, trackScreen } from './events.ts';
import { onBack } from './tg.ts';

export type Screen =
  | { name: 'main' }
  | { name: 'when'; returnTo?: 'checkout' }
  | { name: 'size' }
  | { name: 'swipes' }
  | { name: 'flowers' }
  | { name: 'result' }
  | { name: 'catalog' }
  | { name: 'surprise' }
  | { name: 'checkout' }
  | { name: 'zones' }
  | { name: 'pay'; orderId: string; kind?: 'main' | 'surcharge' }
  | { name: 'order'; orderId: string }
  | { name: 'delivered'; orderId: string }
  | { name: 'giftWho' }
  | { name: 'giftOccasion' }
  | { name: 'giftHow' }
  | { name: 'portrait' }
  | { name: 'recipient'; token: string }
  | { name: 'profile' }
  | { name: 'person'; personId: number; addTaste?: boolean }
  | { name: 'dates' }
  | { name: 'support'; orderId?: string }
  | { name: 'docs' }
  | { name: 'individual' }
  | { name: 'myOrders' };

export const emptyTaste = (): Taste => ({ formsLike: [], formsDislike: [], gammasLike: [], gammasDislike: [], flowersLike: [], flowersDislike: [] });

export interface Flow {
  mode: 'self' | 'gift';
  personId: number | null;
  personName: string | null;
  relation: string | null;
  occasion: string | null;
  date: string | null;
  slotFrom: number | null;
  /** Подарок: день выбран, время уточним у получателя (он выберет его по ссылке вместе с адресом). */
  slotByRecipient?: boolean;
  size: Size;
  taste: Taste;
  picks: PickT[];
  /** Для каких ответов подобрана тройка: пока ответы те же — показываем ту же тройку. */
  picksKey?: string | null;
  design: Design | null;
  surprise: boolean;
  surpriseNotes: string;
  /** Вкус, угаданный по портрету человека: человеку сохраняем, только если букет понравится. */
  portrait?: Taste | null;
  portraitNote?: string;
  /** «Повторить заказ»: чем заполнить оформление. */
  prefill?: Prefill | null;
}

export interface Prefill {
  recipientMode: 'ask' | 'known';
  address: { value: string; lat: number | null; lon: number | null; insideMkad: boolean | null } | null;
  extra: string;
  handover: 'hand' | 'door';
  rName: string;
  rPhone: string;
  cardText: string;
  cardSigned?: boolean;
  cardOpen?: boolean;
  phone?: string;
  email?: string;
  agree?: boolean;
}

const newFlow = (mode: Flow['mode'] = 'self'): Flow => ({
  mode,
  personId: null,
  personName: null,
  relation: null,
  occasion: mode === 'self' ? 'self' : null,
  date: null,
  slotFrom: null,
  size: 'M',
  taste: emptyTaste(),
  picks: [],
  design: null,
  surprise: false,
  surpriseNotes: '',
  portrait: null,
  portraitNote: '',
});

interface Ctx {
  screen: Screen;
  depth: number;
  go: (s: Screen) => void;
  replace: (s: Screen) => void;
  back: () => void;
  home: () => void;
  flow: Flow;
  setFlow: (p: Partial<Flow> | ((f: Flow) => Partial<Flow>)) => void;
  startFlow: (mode: Flow['mode'], p?: Partial<Flow>) => void;
  me: Me | null;
  reloadMe: () => Promise<Me | null>;
  config: AppConfig | null;
  catalog: Design[];
}

const AppCtx = createContext<Ctx>(null as any);
export const useApp = () => useContext(AppCtx);

function load<T>(key: string, fallback: T): T {
  try {
    const v = sessionStorage.getItem(key);
    return v ? (JSON.parse(v) as T) : fallback;
  } catch {
    return fallback;
  }
}
function save(key: string, v: unknown) {
  try {
    sessionStorage.setItem(key, JSON.stringify(v));
  } catch {
    /* приватный режим */
  }
}

/** Куда вернуться, если экран открыли прямо из бота и истории нет. */
function parentOf(s: Screen): Screen | null {
  if (s.name === 'main' || s.name === 'recipient' || s.name === 'myOrders') return null;
  const orderId = 'orderId' in s ? (s as { orderId?: string }).orderId : undefined;
  if (orderId && s.name !== 'order') return { name: 'order', orderId };
  return { name: 'main' };
}

export function AppProvider({ initial, children }: { initial: Screen; children: ReactNode }) {
  const [stack, setStack] = useState<Screen[]>(() => load('vyd.stack', [initial]).length && initial.name === 'main' ? load('vyd.stack', [initial]) : [initial]);
  const [flow, setFlowState] = useState<Flow>(() => load('vyd.flow', newFlow()));
  const [me, setMe] = useState<Me | null>(null);
  const [config, setConfig] = useState<AppConfig | null>(null);
  const [catalog, setCatalog] = useState<Design[]>([]);

  useEffect(() => save('vyd.stack', stack), [stack]);
  // Аналитика: каждый открытый экран пишется сам.
  const top = stack[stack.length - 1];
  useEffect(() => trackScreen(top.name), [top]);
  useEffect(() => save('vyd.flow', flow), [flow]);

  const reloadMe = useCallback(async () => {
    try {
      const m = await api.me();
      setMe(m);
      return m;
    } catch {
      return null;
    }
  }, []);

  useEffect(() => {
    api.config().then(setConfig).catch(() => {});
    api.catalog().then((c) => setCatalog(c.designs)).catch(() => {});
    if (initial.name !== 'recipient') reloadMe();
  }, [initial.name, reloadMe]);

  const go = useCallback((s: Screen) => {
    setStack((st) => [...st, s]);
    window.scrollTo(0, 0);
  }, []);
  const replace = useCallback((s: Screen) => {
    setStack((st) => [...st.slice(0, -1), s]);
    window.scrollTo(0, 0);
  }, []);
  // Экран открыт из уведомления бота и он первый в истории: «назад» ведёт к заказу или на главную.
  // На сайте с самого первого экрана «назад» ведёт на витрину.
  const back = useCallback(
    () =>
      setStack((st) => {
        if (st.length > 1) return st.slice(0, -1);
        const p = parentOf(st[0]);
        if (p) return [p];
        if (onSite && st[0].name !== 'recipient') setTimeout(() => location.assign('/'), 0);
        return st;
      }),
    [],
  );
  const home = useCallback(() => setStack([{ name: 'main' }]), []);
  const setFlow = useCallback((p: Partial<Flow> | ((f: Flow) => Partial<Flow>)) => setFlowState((f) => ({ ...f, ...(typeof p === 'function' ? p(f) : p) })), []);
  const startFlow = useCallback((mode: Flow['mode'], p: Partial<Flow> = {}) => {
    track('flow_started', { mode });
    setFlowState({ ...newFlow(mode), ...p });
  }, []);

  // Системная кнопка «Назад» в Telegram
  const backRef = useRef(back);
  backRef.current = back;
  const canBack = stack.length > 1 || parentOf(stack[0]) !== null;
  useEffect(() => onBack(() => backRef.current(), canBack), [canBack]);

  const value = useMemo<Ctx>(
    () => ({ screen: stack[stack.length - 1], depth: stack.length, go, replace, back, home, flow, setFlow, startFlow, me, reloadMe, config, catalog }),
    [stack, go, replace, back, home, flow, setFlow, startFlow, me, reloadMe, config, catalog],
  );
  return <AppCtx.Provider value={value}>{children}</AppCtx.Provider>;
}
